import { Injectable } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';
import { map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { countries } from '../auth/sign-up/countries';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  modalIsOpen = new BehaviorSubject(false);
  countries = countries;
  private readonly fallbackMapCenter: google.maps.LatLngLiteral = {
    lat: 20,
    lng: 0,
  };
  private approximateUserCenter: google.maps.LatLngLiteral;

  // Add map state preservation
  private savedMapState: {
    center?: google.maps.LatLngLiteral;
    zoom?: number;
  } = {};

  // Add navigation context tracking
  private navigationContext: {
    cameFromMap?: boolean;
    returnRoute?: string;
  } = {};

  constructor(private geocoder: MapGeocoder) {}

  isValidLatLng(location: any): location is google.maps.LatLngLiteral {
    const lat = Number(location?.lat);
    const lng = Number(location?.lng);

    return (
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  }

  normalizeLatLng(location: any): google.maps.LatLngLiteral | null {
    if (!this.isValidLatLng(location)) return null;

    return {
      lat: Number(location.lat),
      lng: Number(location.lng),
    };
  }

  async getBestUserCenter(
    preferredLocation?: any
  ): Promise<google.maps.LatLngLiteral> {
    const savedLocation = this.normalizeLatLng(preferredLocation);
    if (savedLocation) return savedLocation;

    if (this.approximateUserCenter) return this.approximateUserCenter;

    const browserLocation = await this.getBrowserLocation();
    if (browserLocation) {
      this.approximateUserCenter = browserLocation;
      return browserLocation;
    }

    const ipLocation = await this.getIpLocation();
    if (ipLocation) {
      this.approximateUserCenter = ipLocation;
      return ipLocation;
    }

    return this.fallbackMapCenter;
  }

  async getApproximateUserCenter(): Promise<google.maps.LatLngLiteral> {
    if (this.approximateUserCenter) return this.approximateUserCenter;

    const browserLocation = await this.getBrowserLocation();
    if (browserLocation) {
      this.approximateUserCenter = browserLocation;
      return browserLocation;
    }

    const ipLocation = await this.getIpLocation();
    if (ipLocation) {
      this.approximateUserCenter = ipLocation;
      return ipLocation;
    }

    return this.fallbackMapCenter;
  }

  private getBrowserLocation(): Promise<google.maps.LatLngLiteral | null> {
    if (!navigator?.geolocation) return Promise.resolve(null);

    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          const location = this.normalizeLatLng({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          resolve(location);
        },
        () => resolve(null),
        { enableHighAccuracy: false, maximumAge: 600000, timeout: 3000 }
      );
    });
  }

  private async getIpLocation(): Promise<google.maps.LatLngLiteral | null> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    try {
      const response = await fetch('https://ipapi.co/json/', {
        signal: controller.signal,
      });
      if (!response.ok) return null;

      const data = await response.json();
      return this.normalizeLatLng({
        lat: data?.latitude,
        lng: data?.longitude,
      });
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  // Method to save current map state
  saveMapState(center: google.maps.LatLngLiteral, zoom: number) {
    this.savedMapState = { center, zoom };
  }

  // Method to get saved map state
  getSavedMapState() {
    return this.savedMapState;
  }

  // Method to clear saved map state
  clearSavedMapState() {
    this.savedMapState = {};
  }

  // Method to reset map state (for explicit user actions)
  resetMapState() {
    this.savedMapState = {};
  }

  // Method to check if there's a saved map state
  hasSavedMapState(): boolean {
    return !!(this.savedMapState.center && this.savedMapState.zoom);
  }

  // Navigation context methods
  setNavigationContext(cameFromMap: boolean, returnRoute?: string) {
    this.navigationContext = { cameFromMap, returnRoute };
  }

  getNavigationContext() {
    return this.navigationContext;
  }

  clearNavigationContext() {
    this.navigationContext = {};
  }

  // Method to get the appropriate back route
  getBackRoute(defaultRoute: string): string {
    if (
      this.navigationContext.cameFromMap &&
      this.navigationContext.returnRoute
    ) {
      return this.navigationContext.returnRoute;
    }
    return defaultRoute;
  }

  getCoordinatesByUrl(googleMapLink: string) {
    const coordinatesStr = googleMapLink.split('&query=')[1];
    const [lat, lng] = coordinatesStr.split(',');

    return {
      lat: +lat,
      lng: +lng,
    };
  }

  getAddressPosition(address: any) {
    return this.geocoder
      .geocode({
        address: `${address.country}, ${address.city}, ${address.address}`,
      })
      .pipe(
        map(position => {
          console.log(
            position,
            position?.results[0]?.geometry.location.toJSON()
          );
          return position?.results[0]?.geometry.location.toJSON();
        })
      );
  }

  createCoordinateDeviation = coordinate => {
    return coordinate + Math.floor(Math.random() * 10000) / 1000000;
  };

  createCoordinates(googleMapLink) {
    if (!googleMapLink) return;
    const lng = +googleMapLink?.slice(googleMapLink.lastIndexOf(',') + 1);
    const lat = +googleMapLink?.slice(
      googleMapLink.lastIndexOf('=') + 1,
      googleMapLink.lastIndexOf(',')
    );
    return this.normalizeLatLng({ lat, lng });
  }

  async getAddress(googleMapLink) {
    if (googleMapLink) {
      const latLng = this.createCoordinates(googleMapLink);
      const geocoder = new google.maps.Geocoder();
      const address = await geocoder.geocode({
        location: latLng,
        language: 'en-GB',
      });

      const country = address.results[1]?.address_components
        .filter(item => item.types.includes('country'))
        .map(
          item =>
            countries.find(country => country.code === item.short_name).name
        )[0];

      const city = address.results[1]?.address_components.find(
        item =>
          item.types.includes('administrative_area_level_5') ||
          item.types.includes('administrative_area_level_4') ||
          item.types.includes('administrative_area_level_3') ||
          item.types.includes('administrative_area_level_2') ||
          item.types.includes('administrative_area_level_1') ||
          item.types.includes('locality')
      )?.long_name;

      const postalCode = address.results[1]?.address_components.find(item =>
        item.types.includes('postal_code')
      )?.long_name;

      return { country, city, postalCode };
    } else
      return {
        country: '',
        city: '',
        postalCode: '',
      };
  }
}

